// Nurse-to-patient ratio per ward (nursing-ops-2 #08), on the nursing-manager
// bed dashboard. Numerator: nurses ROSTERED on_duty/overtime for today's live
// shift in that ward (planned staffing — deliberately NOT bed assignments,
// which undercount unassigned on-duty nurses and double-count across wards).
// Denominator: occupied beds from the same census the table shows.
//
// The caller passes the roster rows for the shift in progress: BedDashboard
// reads them once and shares them with its "Beds with Nurse" card, so the card
// and the strip always count the same roster.
//
// Visual form (2026-07-27 redesign): a card per ward, worst first, each
// carrying a status pill (icon + word, not color alone — red/green are ~5 ΔE
// apart under deuteranopia) and a raw occupied:nurses count. A Critical
// (unstaffed) card gets a full card border, not just its bottom bar, so it
// visually separates from the grid. The former single overall-ratio summary
// and the shared color legend are dropped: each card is now self-explanatory
// (status pill carries both icon and word), and the overall occupancy/nurse
// totals already live in the separate stat-card row above this panel.
import React, { useMemo } from "react";
import { Box, Paper, Stack, Tooltip, Typography } from "@mui/material";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PersonOffIcon from "@mui/icons-material/PersonOff";

import { NURSE_COLORS } from "../theme";
import { LIMIT, buildWards } from "../wardRatio";

// fill = the card's bottom bar. track = a lighter step of the same hue, so
// the bar reads on white. ink = the icon/word color, dark enough to clear
// 4.5:1 on white. pillBg/pillBorder = the status pill's own treatment.
// Keyed by the same status keys wardRatio.js's statusOf() returns.
const STATUS = {
  adequate: {
    fill: NURSE_COLORS.success, track: "#DCFCE7", ink: "#15803D",
    pillBg: "transparent", pillBorder: "#86EFAC",
    label: "Adequate", Icon: CheckCircleOutlineIcon,
  },
  stretched: {
    fill: NURSE_COLORS.warning, track: "#FEF3C7", ink: "#B45309",
    pillBg: "#FEF3C7", pillBorder: "#FEF3C7",
    label: "Stretched", Icon: WarningAmberIcon,
  },
  over: {
    fill: NURSE_COLORS.danger, track: "#FEE2E2", ink: "#B91C1C",
    pillBg: "transparent", pillBorder: "#FCA5A5",
    label: "Over limit", Icon: ErrorOutlineIcon,
  },
  // Renamed from "No roster" to "Critical" (2026-07-27 redesign) — same
  // statusKey ("unstaffed") as before, label/treatment only.
  unstaffed: {
    fill: NURSE_COLORS.danger, track: "#FEE2E2", ink: "#B91C1C",
    pillBg: "transparent", pillBorder: "#FCA5A5",
    label: "Critical", Icon: PersonOffIcon,
  },
};

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

function WardCard({ w, scaleMax }) {
  const status = STATUS[w.statusKey];
  const { Icon } = status;
  const pct = w.ratio === null ? 100 : Math.max(4, Math.min(100, (w.ratio / scaleMax) * 100));
  const critical = w.statusKey === "unstaffed";

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.75,
        borderRadius: 2,
        borderColor: critical ? "#FECACA" : NURSE_COLORS.border,
        borderWidth: critical ? 2 : 1,
        bgcolor: critical ? "#FEF2F2" : NURSE_COLORS.cardBg,
        minWidth: 0,
      }}
    >
      <Stack direction="row" sx={{ alignItems: "flex-start", justifyContent: "space-between", mb: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap title={w.wardName}>
            {w.wardName}
          </Typography>
        </Box>
        <Stack
          direction="row" spacing={0.5}
          sx={{
            alignItems: "center", flexShrink: 0, px: 1, py: 0.25, borderRadius: 5,
            bgcolor: status.pillBg, border: `1px solid ${status.pillBorder}`,
          }}
        >
          <Icon sx={{ fontSize: 14, color: status.ink }} />
          <Typography variant="caption" sx={{ color: status.ink, fontWeight: 600, whiteSpace: "nowrap" }}>
            {status.label}
          </Typography>
        </Stack>
      </Stack>

      <Tooltip
        title={
          w.ratio === null
            ? `No nurse rostered on duty for this shift — ${plural(w.occupied, "patient")} unattended on the roster`
            : `${plural(w.nurses, "nurse")} rostered on duty for ${plural(w.occupied, "patient")} · limit ${LIMIT}:1`
        }
      >
        <Stack direction="row" spacing={0.75} sx={{ alignItems: "baseline", mb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
            {w.occupied} : {w.nurses}
          </Typography>
          <Typography variant="caption" color="text.secondary">pts : nurses</Typography>
        </Stack>
      </Tooltip>

      <Box sx={{ height: 6, borderRadius: 1, overflow: "hidden", bgcolor: status.track }}>
        <Box sx={{ height: "100%", width: `${pct}%`, bgcolor: status.fill, borderRadius: "0 4px 4px 0" }} />
      </Box>
    </Paper>
  );
}

export default function WardRatioStrip({ beds = [], rosterRows = [], shift }) {
  const wards = useMemo(() => {
    // Ward = (department_id, room_category_code), same denotation as the
    // census, the roster, and the transfer picker. Worst first, so the ward
    // that needs a manager's attention is the first thing read.
    return buildWards(beds, rosterRows).sort((a, b) => {
      if (a.ratio === null || b.ratio === null) return (a.ratio === null ? -1 : 1) - (b.ratio === null ? -1 : 1);
      return b.ratio - a.ratio;
    });
  }, [beds, rosterRows]);

  // One domain for every card, with headroom above the limit, so bar
  // lengths stay comparable across the grid.
  const scaleMax = useMemo(() => {
    const worst = wards.reduce((m, w) => Math.max(m, w.ratio || 0), 0);
    return Math.max(LIMIT * 1.5, Math.ceil(worst * 1.1));
  }, [wards]);

  if (wards.length === 0) return null;

  return (
    <Paper sx={{ p: 2, mb: 2, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 0.5 }}>
        <Box sx={{ width: 28, height: 28, borderRadius: "50%", bgcolor: NURSE_COLORS.accentSoft, display: "grid", placeItems: "center" }}>
          <MonitorHeartIcon sx={{ fontSize: 16, color: NURSE_COLORS.accent }} />
        </Box>
        <Typography variant="subtitle1">Ward staffing ratios — current shift</Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
        Occupied beds per nurse rostered on this shift ({shift}). Anything above {LIMIT}:1 needs attention.
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
          gap: 1.5,
        }}
      >
        {wards.map((w) => (
          <WardCard key={w.wardCode} w={w} scaleMax={scaleMax} />
        ))}
      </Box>
    </Paper>
  );
}
